import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { chatService } from '@services/chatService';
import {
  COMPONENT_PERMISSION_KEYS,
  COMPONENT_TYPE_ORDER,
  type ChatComponentItem,
  type ComponentType,
} from '../types/chat-component';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

export const COMPONENT_TAB_META: Record<
  ComponentType,
  { key: ComponentType; label: string }
> = {
  order: { key: 'order', label: 'Đơn hàng' },
  product: { key: 'product', label: 'Sản phẩm' },
  ingredient: { key: 'ingredient', label: 'Nguyên liệu' },
  customer: { key: 'customer', label: 'Khách hàng' },
  supplier: { key: 'supplier', label: 'Nhà cung cấp' },
  employee: { key: 'employee', label: 'Nhân viên' },
};

type TabCache = {
  items: ChatComponentItem[];
  page: number;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  keyword: string;
};

const emptyTabCache = (): TabCache => ({
  items: [],
  page: 0,
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: null,
  keyword: '',
});

export const getAllowedComponentTypes = (
  permissions?: Record<string, boolean> | null,
  isOwner?: boolean,
): ComponentType[] => {
  if (isOwner) return [...COMPONENT_TYPE_ORDER];
  return COMPONENT_TYPE_ORDER.filter((type) => Boolean(permissions?.[COMPONENT_PERMISSION_KEYS[type]]));
};

interface UseComponentPickerArgs {
  open: boolean;
  permissions?: Record<string, boolean> | null;
  isOwner?: boolean;
}

export const useComponentPicker = ({
  open,
  permissions,
  isOwner = false,
}: UseComponentPickerArgs) => {
  const allowedTypes = useMemo(
    () => getAllowedComponentTypes(permissions, isOwner),
    [permissions, isOwner],
  );

  const [selectedType, setSelectedType] = useState<ComponentType>('order');
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [cache, setCache] = useState<Partial<Record<ComponentType, TabCache>>>({});
  const [tokenReady, setTokenReady] = useState(false);
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  const listRequestIdRef = useRef(0);
  const componentTokenRef = useRef<string | null>(null);
  // true: dùng token business đã map + API production; false: không có mapping → token cha + API thường
  const isMappedTokenRef = useRef(false);

  useEffect(() => {
    if (!allowedTypes.length) return;
    setSelectedType((prev) => (allowedTypes.includes(prev) ? prev : allowedTypes[0]));
  }, [allowedTypes]);

  // Resolve mapped business token when picker opens
  useEffect(() => {
    if (!open) {
      componentTokenRef.current = null;
      isMappedTokenRef.current = false;
      setTokenReady(false);
      return;
    }

    let cancelled = false;
    const parentToken = sessionStorage.getItem('access_token');
    if (!parentToken) {
      componentTokenRef.current = null;
      setTokenReady(false);
      return;
    }

    setTokenReady(false);
    void (async () => {
      const resolved = await chatService.resolveComponentAccessToken(parentToken);
      if (cancelled) return;
      console.log('[ComponentPicker] token ready for list APIs', {
        hasMappedToken: Boolean(resolved),
        resolvedPreview: resolved?.slice(0, 20) ?? null,
      });
      // Không có mapping → dùng luôn token cha với API của môi trường hiện tại
      componentTokenRef.current = resolved || parentToken;
      isMappedTokenRef.current = Boolean(resolved);
      setTokenReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const updateTabCache = useCallback((type: ComponentType, patch: Partial<TabCache>) => {
    setCache((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] || emptyTabCache()),
        ...patch,
      },
    }));
  }, []);

  const fetchTab = useCallback(
    async (type: ComponentType, options?: { page?: number; keyword?: string; append?: boolean }) => {
      const token = componentTokenRef.current;
      if (!token) {
        updateTabCache(type, {
          items: [],
          page: 0,
          hasMore: false,
          loading: false,
          loadingMore: false,
          error: null,
          keyword: options?.keyword || '',
        });
        return;
      }

      const page = options?.page ?? 1;
      const keyword = options?.keyword ?? '';
      const append = Boolean(options?.append);
      const requestId = ++listRequestIdRef.current;

      updateTabCache(type, {
        loading: !append,
        loadingMore: append,
        error: null,
        keyword,
      });

      try {
        const result = await chatService.listChatComponents(
          token,
          type,
          { page, pageSize: PAGE_SIZE, keyword },
          isMappedTokenRef.current,
        );

        if (requestId !== listRequestIdRef.current && !append) {
          return;
        }

        setCache((prev) => {
          const current = prev[type] || emptyTabCache();
          return {
            ...prev,
            [type]: {
              items: append ? [...current.items, ...result.items] : result.items,
              page: result.page,
              hasMore: result.hasMore,
              loading: false,
              loadingMore: false,
              error: null,
              keyword,
            },
          };
        });
      } catch {
        if (requestId !== listRequestIdRef.current && !append) {
          return;
        }
        updateTabCache(type, {
          loading: false,
          loadingMore: false,
          error: 'Không tải được danh sách',
          ...(append ? {} : { items: [], page: 0, hasMore: false }),
          keyword,
        });
      }
    },
    [updateTabCache],
  );

  // Reset when closing; do not prefetch all types on open
  useEffect(() => {
    if (!open) {
      setCache({});
      setSearchInput('');
      setSearchKeyword('');
    }
  }, [open]);

  // Debounce search input → keyword for active tab
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setSearchKeyword(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, open]);

  // Search refetch for active tab only (after mapped token is ready)
  useEffect(() => {
    if (!open || !tokenReady) return;
    const tab = cacheRef.current[selectedType];

    if (searchKeyword === '') {
      if (tab?.loading) return;
      // Already loaded empty-keyword page (or selectType already fetching)
      if (tab && tab.keyword === '' && (tab.page > 0 || tab.items.length > 0 || Boolean(tab.error))) {
        return;
      }
    } else if (tab && tab.keyword === searchKeyword && (tab.page > 0 || tab.loading)) {
      return;
    }

    void fetchTab(selectedType, { page: 1, keyword: searchKeyword, append: false });
  }, [open, tokenReady, selectedType, searchKeyword, fetchTab]);

  const activeCache = cache[selectedType] || emptyTabCache();

  const loadMore = useCallback(() => {
    if (!open || !tokenReady) return;
    if (activeCache.loading || activeCache.loadingMore || !activeCache.hasMore) return;
    void fetchTab(selectedType, {
      page: activeCache.page + 1,
      keyword: searchKeyword,
      append: true,
    });
  }, [open, tokenReady, activeCache, selectedType, searchKeyword, fetchTab]);

  const selectType = useCallback(
    (type: ComponentType) => {
      setSelectedType(type);
      setSearchInput('');
      setSearchKeyword('');
      if (!tokenReady) return;
      void fetchTab(type, { page: 1, keyword: '', append: false });
    },
    [fetchTab, tokenReady],
  );

  const commitSearch = useCallback(() => {
    setSearchKeyword(searchInput.trim());
  }, [searchInput]);

  return {
    allowedTypes,
    selectedType,
    selectType,
    searchInput,
    setSearchInput,
    commitSearch,
    activeCache,
    loadMore,
    tabMeta: COMPONENT_TAB_META,
    tokenReady,
  };
};
